<template>
    <div>
        <m-pageframe :title="uiPageTitle">
            <v-container fluid style="padding: 15px">
                <v-card-title>
                    <v-btn
                        icon
                        @click="goBackToImportSearch"
                        style="margin-right: 10px;"
                        :title="uiBackToHistoryTitle"
                    >
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-icon left>mdi-file-upload</v-icon>
                    {{ uiPageTitle }}
                </v-card-title>

                <v-card-text>
                <!-- New Import Experience Alert -->
                <v-alert
                    v-if="overrideImportType === 'bulk-message-recipients' && $rc.isBetaFeatureNew('beta-bulk-message-import-v2')"
                    type="info"
                    border="left"
                    class="shaded-light mb-4"
                    colored-border
                    dense
                >
                    <div class="text-h6 mb-2">Meet the New Import Experience</div>
                    <div class="mb-2">We've added smarter enhancements to improve how you import recipients.</div>
                    <rc-link
                        href="https://redchirp.com/meet-the-new-bulk-message-import-experience/"
                        text="See what's new"
                        open_in_new_tab
                    />
                </v-alert>

                <!-- Staff real account warning -->
                <v-alert v-if="$rc.$state.isAuthActorSystemAdmin() && !$rc.$state.isDemoOrTestOrganization()"
                    type="warning" style="margin-bottom: 20px;">
                    <div><strong>Warning</strong></div>
                    <div>This is a real customer account. Changes made here will affect production data.</div>
                </v-alert>

                <!-- Read-Only Mode Alert -->
                <v-alert v-if="is_read_only_mode && !$rc.$state.isAuthActorSystemAdmin()" type="info"
                    style="margin-bottom: 20px;">
                    <div><strong>Read-Only Access</strong></div>
                    <div>
                        You have read-only access to contact imports. Only organization administrators can create or
                        modify imports.
                    </div>
                </v-alert>

                <!-- Target Information Card -->
                <v-card v-if="computedTargetListName || computedTargetBulkMessageName" class="mb-4 elevation-2">
                    <v-card-title class="pb-2">
                        <v-icon v-if="computedTargetListName" color="purple"
                            class="mr-2">mdi-format-list-bulleted</v-icon>
                        <v-icon v-if="computedTargetBulkMessageName" color="blue"
                            class="mr-2">mdi-email-multiple</v-icon>
                        {{ computedTargetListName ? 'Target List' : 'Target Bulk Message' }}
                    </v-card-title>
                    <v-card-text class="pt-0">
                        <div class="text-h6">{{ computedTargetListName || computedTargetBulkMessageName }}</div>
                    </v-card-text>
                </v-card>

                <!-- Wizard Stepper -->
                <div class="import-stepper-wrapper" style="position: relative;">
                    <v-stepper
                        v-model="current_step"
                        alt-labels
                        :class="{ 'removal-stepper': isRemoveMode }"
                        style="margin-bottom: 20px;"
                    >
                    <v-stepper-header>
                        <v-stepper-step
                            :complete="current_step > 1"
                            step="1"
                            :editable="canEditStep(1)"
                            @click="goToStep(1)"
                        >
                            {{ uiStepUploadTitle }}
                            <small v-if="import_meta_data">{{ import_meta_data.original_file_name }}</small>
                        </v-stepper-step>

                        <v-divider></v-divider>

                        <v-stepper-step :complete="current_step > 2" step="2" :editable="canEditStep(2)"
                            @click="goToStep(2)">
                            Column Mapping
                            <small>Map your columns</small>
                        </v-stepper-step>

                        <v-divider></v-divider>

                        <v-stepper-step
                            :complete="current_step > 3"
                            step="3"
                            :editable="canEditStep(3)"
                            @click="goToStep(3)"
                        >
                            {{ uiStepReviewTitle }}
                            <small v-if="preview_data">{{ preview_data.total_records || 0 }} records</small>
                        </v-stepper-step>

                        <v-divider></v-divider>

                        <v-stepper-step step="4" :editable="false">
                            {{ uiStepCompleteTitle }}
                        </v-stepper-step>
                    </v-stepper-header>

                    <v-stepper-items>
                        <!-- Step 1: Upload File -->
                        <v-stepper-content step="1">
                        <div style="max-width: 500px; margin: 0 auto;" v-if="should_show_loading_screen">
                                <v-progress-linear indeterminate color="primary" height="12" rounded class="mb-3">
                                </v-progress-linear>
                                <div style="font-size: 14px; color: #666;">
                                    Loading {{ actionNounLower }} data...
                                </div>
                            </div>
                            <!-- Instructions and Upload Form (when not uploading) -->
                            <div v-else-if="!uploading">
                                <div style="margin-bottom: 20px;">
                                    <h3>Instructions</h3>
                                    <p>
                                        {{ uiInstructionsText }} {{ uiInstructionsFollowUp }}
                                    </p>

                                    <p>
                                        <strong>Important:</strong> There is a re-mapping step but the closer you can
                                        get your file to match the format
                                        below, the easier the {{ actionNounLower }} will be. Below is a template you can use to get
                                        started.
                                    </p>

                                    <div style="margin: 15px 0;">
                                        <v-btn color="secondary" v-on:click="onClickDownloadTemplate" outlined>
                                            <v-icon left>mdi-download</v-icon>
                                            Download Template File
                                        </v-btn>
                                    </div>
                                </div>

                                <!-- Field Requirements Section -->
                                <div style="margin-bottom: 20px;">
                                    <h3 style="margin-bottom: 10px;">
                                        {{ uiFormatRequirementsTitle }}
                                    </h3>
                                    <p style="margin-bottom: 15px; color: #666;">
                                        Your Excel file must contain columns with data for all the required columns
                                        below. Optional fields can be included to provide additional contact
                                        information.
                                    </p>

                                    <v-card outlined>
                                        <v-simple-table>
                                            <template v-slot:default>
                                                <thead>
                                                    <tr style="background-color: #f5f5f5;">
                                                        <th class="text-left" style="font-weight: 600;">Column Name</th>
                                                        <th class="text-left" style="font-weight: 600;">Required</th>
                                                        <th class="text-left" style="font-weight: 600;">Format</th>
                                                        <th class="text-left" style="font-weight: 600;">Example</th>
                                                        <th class="text-left" style="font-weight: 600;">Description</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td><strong>Phone Number</strong></td>
                                                        <td>
                                                            <v-chip small color="red"
                                                                text-color="white">Required</v-chip>
                                                        </td>
                                                        <td>Phone format</td>
                                                        <td>555-123-4567<br />(555) 123-4567</td>
                                                        <td>Primary phone number for the contact</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>First Name</strong></td>
                                                        <td>
                                                            <v-tooltip top>
                                                                <template v-slot:activator="{ on, attrs }">
                                                                    <v-chip small color="red" text-color="white"
                                                                        v-bind="attrs" v-on="on">Required &nbsp;<v-icon
                                                                            small>mdi-information-outline</v-icon></v-chip>
                                                                </template>
                                                                <span>Selecting a first name column will be required;
                                                                    however, if a row is missing a first name it will
                                                                    still be
                                                                    accepted.</span>
                                                            </v-tooltip>
                                                        </td>
                                                        <td>Text</td>
                                                        <td>John</td>
                                                        <td>Contact's first name</td>
                                                    </tr>
                                                    <tr>
                                                        <td><strong>Last Name</strong></td>
                                                        <td>
                                                            <v-tooltip top>
                                                                <template v-slot:activator="{ on, attrs }">
                                                                    <v-chip small color="red" text-color="white"
                                                                        v-bind="attrs" v-on="on">Required &nbsp;<v-icon
                                                                            small>mdi-information-outline</v-icon></v-chip>
                                                                </template>
                                                                <span>Selecting a last name column will be required;
                                                                    however, if a row is missing a last name it will
                                                                    still
                                                                    be accepted.</span>
                                                            </v-tooltip>
                                                        </td>
                                                        <td>Text</td>
                                                        <td>Doe</td>
                                                        <td>Contact's last name</td>
                                                    </tr>
                                                    <tr v-if="!isRemoveMode">
                                                        <td><strong>Email</strong></td>
                                                        <td>
                                                            <v-chip small color="grey lighten-3">Optional</v-chip>
                                                        </td>
                                                        <td>Email format</td>
                                                        <td>john.doe@example.com</td>
                                                        <td>Contact's email address</td>
                                                    </tr>
                                                    <tr v-if="!isRemoveMode">
                                                        <td><strong>Birthdate</strong></td>
                                                        <td>
                                                            <v-chip small color="grey lighten-3">Optional</v-chip>
                                                        </td>
                                                        <td>Date format</td>
                                                        <td>12/25/1990<br />1990-12-25</td>
                                                        <td>Contact's birthdate (supports multiple date formats)</td>
                                                    </tr>
                                                    <tr v-if="!isRemoveMode">
                                                        <td><strong>Notes</strong></td>
                                                        <td>
                                                            <v-chip small color="grey lighten-3">Optional</v-chip>
                                                        </td>
                                                        <td>Text</td>
                                                        <td>Contact note</td>
                                                        <td>Additional notes about the contact</td>
                                                    </tr>
                                                </tbody>
                                            </template>
                                        </v-simple-table>
                                    </v-card>

                                </div>

                                <v-divider style="margin: 20px 0;"></v-divider>

                                <!-- File Upload Section -->
                                <div v-if="has_write_access && (!import_id || import_state === 'uploaded')">
                                    <h4>Upload Excel File</h4>

                                    <v-file-input v-model="excel_file" show-size accept=".xlsx"
                                        label="Excel File (XLSX)" style="padding-bottom: 15px; max-width: 500px;"
                                        :rules="file_rules" :disabled="uploading" />

                                    <div style="margin-bottom: 20px;">
                                        <v-btn color="primary" v-on:click="onClickUploadFile"
                                            :disabled="!can_upload_file" :loading="uploading">
                                            <v-icon left>mdi-upload</v-icon>
                                            Upload File
                                        </v-btn>
                                    </div>
                                    <div v-if="upload_errors.length > 0">
                                        <v-alert type="error" style="margin-bottom: 15px;">
                                            <div><strong>Upload Errors:</strong></div>
                                            <ul>
                                                <li v-for="error in upload_errors" :key="error">{{ error }}</li>
                                            </ul>
                                        </v-alert>
                                    </div>
                                </div>
                            </div>

                            <!-- Upload Progress -->
                            <ImportProcessProgress v-if="uploading" title="Processing Upload..."
                                :description="uiUploadProgressDescription" :process-id="upload_process_id"
                                :import-type="overrideImportType || 'contact'"
                                :organization-id="$route.params.organization_id"
                                default-status-message="Uploading your file..." @complete="onUploadComplete"
                                @failed="onUploadFailed" />

                            <!-- Upload Failed -->
                            <div v-else-if="intermediate_process_errors.length > 0">
                                <v-alert type="error" style="margin-bottom: 20px;"
                                    v-if="intermediate_process_errors.length > 0">
                                    <div v-for="error in intermediate_process_errors" :key="error">
                                        {{ error }}. Please try again.
                                    </div>
                                </v-alert>
                                <v-alert type="error" style="margin-bottom: 20px;" v-else>
                                    Unable to load sheet information. Please try uploading the file again.
                                </v-alert>
                                <v-btn color="secondary" v-on:click="startNewImport">
                                    <v-icon left>mdi-upload</v-icon>
                                    {{ uiStartNewActionLabel }}
                                </v-btn>
                            </div>

                        </v-stepper-content>

                        <!-- Step 2: Column Mapping -->
                        <v-stepper-content step="2">
                            <div v-if="sheet_information">
                                <ColumnMappingComponent :available-sheets="sheet_information.available_sheets"
                                    :sheet-metadata-map="sheet_metadata_map"
                                    :required-fields="computedRequiredFields" :optional-fields="computedOptionalFields"
                                    :importType="import_type" :suggested-mapping="suggestedMapping"
                                    :sample-data="sheet_information.available_sheets" :saving="saving_column_mapping"
                                    @mapping-changed="onColumnMappingChanged"
                                    @can-continue-changed="onCanContinueChanged"
                                    @continue-to-preview="onContinueToPreview"
                                    @sheet-changed="onColumnMappingSheetChanged" />
                            </div>
                            <div v-else-if="loading_sheet_information" style="text-align: center; padding: 40px 0;">
                                <div style="max-width: 500px; margin: 0 auto;">
                                    <v-progress-linear indeterminate color="primary" height="12" rounded class="mb-3">
                                    </v-progress-linear>
                                    <div style="font-size: 14px; color: #666;">
                                        Analyzing your Excel file structure...
                                    </div>
                                </div>
                            </div>

                            <div v-else>
                                <v-alert type="error" style="margin-bottom: 20px;"
                                    v-if="intermediate_process_errors.length > 0">
                                    <div v-for="error in intermediate_process_errors" :key="error">
                                        {{ error }}. Please try again.
                                    </div>
                                    <v-btn color="secondary" v-on:click="startNewImport">
                                        <v-icon left>mdi-upload</v-icon>
                                        {{ uiStartNewActionLabel }}
                                    </v-btn>
                                </v-alert>
                                <v-alert type="error" style="margin-bottom: 20px;" v-else>
                                    Unable to load sheet information. Please try uploading the file again.
                                    <v-btn color="secondary" v-on:click="startNewImport">
                                        <v-icon left>mdi-upload</v-icon>
                                        {{ uiStartNewActionLabel }}
                                    </v-btn>
                                </v-alert>

                            </div>
                            <ImportProcessProgress v-if="saving_column_mapping && intermediate_process_id"
                                title="Creating Preview..."
                                description="Please wait while we process your column mapping and create a preview."
                                :process-id="intermediate_process_id" :importType="overrideImportType || 'contact'"
                                :organization-id="$route.params.organization_id"
                                default-status-message="Processing column mapping..." @complete="onIntermediateComplete"
                                @failed="onIntermediateFailed" />


                            <!-- Step 2 Navigation -->

                            <v-card-actions v-if="!loading_sheet_information && !saving_column_mapping" style="padding: 0; margin-top: 20px;">
                                <v-spacer></v-spacer>

                                <v-btn color="primary" @click="onContinueToPreview"
                                    :disabled="!canContinueToPreview">
                                    {{ uiContinueToPreviewLabel }}
                                    <v-icon right>mdi-arrow-right</v-icon>
                                </v-btn>
                            </v-card-actions>
                        </v-stepper-content>

                        <!-- Step 3: Review Preview -->
                        <v-stepper-content step="3">

                            <div v-if="generating_preview" style="text-align: center; padding: 40px 0;">
                                <div style="max-width: 500px; margin: 0 auto;">
                                    <v-progress-linear indeterminate color="primary" height="12" rounded class="mb-3">
                                    </v-progress-linear>
                                    <div style="font-size: 14px; color: #666;">
                                        Generating preview...
                                    </div>
                                </div>
                            </div>

                            <div v-else-if="preview_failed">
                                <v-alert type="error" prominent border="left" class="mb-4">
                                    <div class="text-h6">Preview Generation Failed</div>
                                    <div>{{ preview_error_message }}</div>
                                </v-alert>
                                <v-btn color="primary" @click="startPreviewGeneration" :loading="generating_preview">
                                    <v-icon left>mdi-refresh</v-icon>
                                    Retry Preview
                                </v-btn>
                            </div>

                            <div v-else-if="preview_ready">
                                <v-alert v-if="import_state === 'cancelled'" type="warning" prominent border="left">
                                    <div style="display: flex; align-items: center; justify-content: space-between;">
                                        <div>
                                            <div><strong>{{ actionNoun }} Cancelled</strong></div>
                                            <div>This {{ actionNounLower }} has been cancelled and cannot be continued. You can
                                                view the preview below but
                                                cannot proceed to execution.</div>
                                        </div>
                                        <v-btn color="white" outlined style="margin-left: 20px;"
                                            v-on:click="startNewImport">
                                            <v-icon left>mdi-upload</v-icon>
                                            {{ uiStartNewActionLabel }}
                                        </v-btn>
                                    </div>
                                </v-alert>
                                <v-alert v-else-if="import_state === 'failed'" type="error" prominent border="left">
                                    <div style="display: flex; align-items: center; justify-content: space-between;">
                                        <div>
                                            <div><strong>Unexpected {{ actionNoun }} Error</strong></div>
                                            <div>Please retry with a new {{ actionNounLower }}.</div>
                                        </div>
                                        <v-btn color="white" outlined style="margin-left: 20px;"
                                            v-on:click="startNewImport">
                                            <v-icon left>mdi-upload</v-icon>
                                            {{ uiStartNewActionLabel }}
                                        </v-btn>
                                    </div>
                                </v-alert>

                                <!-- Warning: Bulk message no longer in EDIT state -->
                                <v-alert v-if="isBulkMessageRecipientsImport && targetBulkMessage && targetBulkMessage.state !== 'edit'"
                                    type="warning" prominent border="left" class="mb-4">
                                    <div style="display: flex; align-items: center; justify-content: space-between;">
                                        <div>
                                            <div><strong>Bulk Message Not in Edit State</strong></div>
                                            <div>The target bulk message is currently in <strong>{{ targetBulkMessage.state }}</strong> state.
                                                You must return the bulk message to the <strong>edit</strong> state before you can {{ actionVerbLower }} recipients.</div>
                                        </div>
                                        <v-btn color="white" outlined style="margin-left: 20px;"
                                            :to="{ name: 'view-bulk-message', params: { organization_id: $route.params.organization_id, bulk_message_id: targetBulkMessageId }}">
                                            <v-icon left>mdi-email-edit</v-icon>
                                            Go to Bulk Message
                                        </v-btn>
                                    </div>
                                </v-alert>

                                <v-expansion-panels v-model="expansion_panel" v-if="import_state !== 'cancelled' && import_state !== 'failed'" class="mb-4">
                                    <v-expansion-panel>
                                        <v-expansion-panel-header color="grey lighten-4">
                                            <template v-slot:default="">
                                                <v-row no-gutters class="align-center">
                                                    <v-col cols="auto">
                                                        <v-icon color="grey darken-2"
                                                            class="mr-4">mdi-alert-circle-outline</v-icon>
                                                    </v-col>
                                                    <v-col>
                                                        <div class="text-subtitle-1 font-weight-medium">{{ uiReviewPanelTitle }}</div>
                                                    </v-col>
                                                </v-row>
                                            </template>
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content class="pt-4">
                                            <v-list dense>
                                                <!-- Remove mode specific items -->
                                                <template v-if="isRemoveMode">
                                                    <v-list-item class="px-0">
                                                        <v-list-item-icon class="mr-4">
                                                            <v-icon color="success">mdi-shield-check</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-content>
                                                            <v-list-item-title>
                                                                <span class="font-weight-medium">Contact records are preserved as is</span> -
                                                                they will not be created or updated.
                                                            </v-list-item-title>
                                                            <v-list-item-subtitle>
                                                                Only the association between a phone number and this bulk message is removed.
                                                            </v-list-item-subtitle>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                    <v-list-item class="px-0">
                                                        <v-list-item-icon class="mr-4">
                                                            <v-icon color="info">mdi-information</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-content>
                                                            <v-list-item-title>
                                                                <span class="font-weight-medium">Non-matching rows</span> will be 
                                                                skipped without error.
                                                            </v-list-item-title>
                                                            <v-list-item-subtitle>
                                                                Phone numbers not in the recipient list are ignored.
                                                            </v-list-item-subtitle>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                </template>

                                                <!-- Add mode specific items -->
                                                <template v-else>
                                                    <v-list-item class="px-0">
                                                        <v-list-item-icon class="mr-4">
                                                            <v-icon color="success">mdi-account-plus</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-content>
                                                            <v-list-item-title>
                                                                <span class="font-weight-medium">New contacts</span> will be
                                                                created if no phone
                                                                number
                                                                match is found.
                                                            </v-list-item-title>
                                                            <v-list-item-subtitle>
                                                                See "System Notes" for details.
                                                            </v-list-item-subtitle>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                    <v-list-item class="px-0">
                                                        <v-list-item-icon class="mr-4">
                                                            <v-icon color="info">mdi-account-edit</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-content>
                                                            <v-list-item-title>
                                                                <span class="font-weight-medium">Existing contacts</span>
                                                                will be
                                                                updated only if data has changed.
                                                            </v-list-item-title>
                                                            <v-list-item-subtitle>
                                                                See "System Notes" for a list of changes.
                                                            </v-list-item-subtitle>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                    <v-list-item class="px-0">
                                                        <v-list-item-icon class="mr-4">
                                                            <v-icon color="warning">mdi-cancel</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-content>
                                                            <v-list-item-title>
                                                                <span class="font-weight-medium">Rows with errors</span>
                                                                will be
                                                                skipped.
                                                            </v-list-item-title>
                                                            <v-list-item-subtitle>
                                                                Refer to the "Errors" column for specific reasons.
                                                            </v-list-item-subtitle>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                </template>

                                                <v-list-item v-if="importTypeSpecificMessage" class="px-0">
                                                    <v-list-item-icon class="mr-4">
                                                        <v-icon :color="importTypeSpecificMessage.color">{{
                                                            importTypeSpecificMessage.icon }}</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-content>
                                                        <v-list-item-title class="font-weight-medium">
                                                            {{ importTypeSpecificMessage.title }}
                                                        </v-list-item-title>
                                                        <v-list-item-subtitle>
                                                            {{ importTypeSpecificMessage.subtitle }}
                                                        </v-list-item-subtitle>
                                                    </v-list-item-content>
                                                </v-list-item>

                                            </v-list>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>

                                <v-alert color="warning"  text v-if="above_warn_change_contact_threshold"
                                    class="mb-4">
                                    <div v-html="above_contact_threshold_warning_message"></div>
                                </v-alert>
                                <v-alert type="info"  text v-if="isCanadianOrganization && used_phone_validation"
                                    class="mb-4">
                                    For Canadian phone numbers, text message eligibility cannot be verified in advance. The number will be treated as valid, but the first message may be rejected by the carrier if the number cannot receive texts. 
                                </v-alert>

                                <v-card outlined class="mb-4">
                                    <v-card-text class="py-2 d-flex align-center">
                                        <v-icon class="ml-1 mr-3">{{ isExcelImport ? 'mdi-file-excel' : 'mdi-database-import' }}</v-icon>
                                        <div v-if="isExcelImport" class="text-subtitle-1 font-weight-medium">
                                            Selected Sheet: <span>'{{ sheet_name || 'Not specified' }}'</span>
                                        </div>
                                        <div v-else-if="import_meta_data && import_meta_data.import_data_source" class="text-subtitle-1 font-weight-medium">
                                            Data Source: <span>{{ $rc.pretty(import_meta_data.import_data_source, 'enum_import_data_source') }}</span>
                                        </div>
                                        <v-spacer></v-spacer>
                                        <v-btn small text color="primary"
                                            v-if="import_meta_data && import_meta_data.enriched_data_blob_id"
                                            @click="onClickDownloadEnrichedDataFromPreview">
                                            <v-icon left small>mdi-file-excel</v-icon>
                                            Export to Excel
                                        </v-btn>
                                    </v-card-text>
                                </v-card>

                                <ContactImportPreviewTable v-if="preview_data && !generating_preview && !preview_failed"
                                    :preview-data="preview_data" :show-summary="true"
                                    :summary-cards="uiContactSummaryCards" :custom-columns="uiContactCustomColumns"
                                    :table-headers="uiContactTableHeaders" :items-per-page="25" :hide-footer="false"
                                    :loading="false" :auto-filter-to-errors="false" :selected-rows="selected_rows"
                                    :select-all="select_all" @toggle-row-selection="toggleRowSelection"
                                    @toggle-select-all="toggleSelectAll" />
                            </div>

                            <v-card-actions v-if="preview_ready && !generating_preview && !importing" class="mt-4 pa-0" :style="isBulkMessageRecipientsImport && !isRemoveMode ? 'margin-bottom: 24px;' : ''">

                                <!-- Greyed out "Validate Unknown Numbers" for bulk message recipients import (moved to Import Tools) -->
                                <div v-if="isBulkMessageRecipientsImport && !isRemoveMode" style="position: relative; overflow: visible;">
                                    <v-btn disabled>
                                        <v-icon left>mdi-check-decagram</v-icon>
                                        Validate Unknown Numbers
                                    </v-btn>
                                    <span class="text-caption text--secondary" style="position: absolute; left: 0; top: 100%; margin-top: 4px; white-space: nowrap;">
                                        <em>This option has moved to "{{ uiToolsLabel }}"</em>
                                    </span>
                                </div>

                                <!-- Import tools only when not in remove mode -->
                                <rc-feature-discovery-tooltip
                                    v-if="!isRemoveMode"
                                    ref="gentle_caps_tooltip"
                                    storage_key="gentle-name-capitalization-v1"
                                    message="New feature! Gently correct name capitalization in your imports."
                                    arrow_position="bottom-left"
                                    :disabled="!isGentleCapsFeatureNew"
                                >
                                    <template #target>
                                        <v-menu offset-y top @input="onImportToolsMenuToggle">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn color="secondary" class="ml-2" v-bind="attrs" v-on="on"
                                                    :loading="validating_phone_numbers"
                                                    :disabled="validating_phone_numbers || !has_write_access || !can_cancel_import">
                                                    <v-icon left>mdi-tools</v-icon>
                                                    {{ uiToolsLabel }}
                                                    <v-icon right>mdi-menu-up</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list dense>
                                                <!-- Phone Tools Section -->
                                                <v-subheader>Phone Tools</v-subheader>
                                                <v-list-item @click="validateUnknownPhoneNumbers">
                                                    <v-list-item-icon><v-icon>mdi-check-decagram</v-icon></v-list-item-icon>
                                                    <v-list-item-title>Validate Unknown Numbers</v-list-item-title>
                                                </v-list-item>
                                                <!-- We don't want folks to bypass phone validation on bulk message recipients since the direct intention is to send messages -->
                                                <v-list-item v-if="!isBulkMessageRecipientsImport"
                                                    @click="allowInvalidPhoneNumbers">
                                                    <v-list-item-icon><v-icon>mdi-alert-circle-outline</v-icon></v-list-item-icon>
                                                    <v-list-item-title>Allow Invalid Numbers</v-list-item-title>
                                                </v-list-item>
                                                
                                                <v-divider></v-divider>
                                                
                                                <!-- Name Tools Section -->
                                                <v-subheader>Name Tools</v-subheader>
                                                <v-list-item @click="showGentleNameCapitalizationDialog">
                                                    <v-list-item-icon><v-icon>mdi-format-letter-case</v-icon></v-list-item-icon>
                                                    <v-list-item-title>Gently Correct Name Capitalization...<m-menu-chip :beta_feature_code="beta_feature_codes.gentle_name_capitalization" /></v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </template>
                                </rc-feature-discovery-tooltip>

                                <span v-if="phone_validation_status"
                                    style="color: #666; font-size: 14px; margin-left: 15px;">
                                    {{ phone_validation_status }}
                                </span>
                                                                
                                <v-spacer></v-spacer>

                                <v-btn color="error" outlined @click="cancelImport" class="mr-2"
                                    v-if="can_cancel_import && has_write_access">
                                    <v-icon left>mdi-cancel</v-icon> Cancel
                                </v-btn>

                                <v-tooltip bottom :disabled="canProceedToExecution">
                                    <template v-slot:activator="{ on, attrs }">
                                        <div v-bind="attrs" v-on="on">
                                            <v-btn color="primary" :disabled="!canProceedToExecution"
                                                @click="executeImport" :loading="importing">
                                                <v-icon left>mdi-play</v-icon> Run {{ actionNoun }}
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>{{ executionDisabledReason }}</span>
                                </v-tooltip>
                            </v-card-actions>
                        </v-stepper-content>

                        <!-- Step 4: Import/Removal Complete -->
                        <v-stepper-content step="4">
                            <!-- Import Execution Progress -->
                            <ImportProcessProgress v-if="importing && import_process_id"
                                :title="uiExecutionProgressTitle" :description="uiExecutionProgressDescription"
                                :process-id="import_process_id" :import-type="overrideImportType || 'contact'"
                                :organization-id="$route.params.organization_id"
                                :default-status-message="uiExecutionStatusMessage" @complete="onExecutionComplete"
                                @failed="onExecutionFailed" />
                            <div v-else-if="execution_errors.length > 0">
                                <v-alert type="error" style="margin-bottom: 20px;">
                                    <div><strong>{{ actionNoun }} Failed Unexpectedly</strong></div>
                                    <div v-for="error in execution_errors" :key="error">{{ error }}</div>
                                </v-alert>
                                <v-btn color="secondary" v-on:click="startNewImport">
                                    <v-icon left>mdi-upload</v-icon>
                                    {{ uiStartNewActionLabel }}
                                </v-btn>
                            </div>

                            <!-- Import/Removal Complete -->
                            <div v-else-if="!importing" style="text-align: center; padding: 40px 0;">
                                <v-icon color="success" size="80">mdi-check-circle</v-icon>

                                <h3 class="success--text" style="margin-top: 20px;">
                                    {{ uiCompletionTitle }}
                                </h3>

                                <p style="margin-top: 10px; color: #666;">
                                    {{ uiCompletionDescription }}
                                </p>

                                <!-- Navigation Options -->
                                <div style="margin-top: 30px;">
                                    <h4 style="margin-bottom: 20px; color: #333;">What would you like to do next?
                                    </h4>
                                    <div
                                        style="display: flex; flex-direction: column; gap: 15px; max-width: 600px; margin: 0 auto;">

                                        <!-- Primary Action Button (context-specific) -->
                                        <v-btn color="primary" large @click="goToPrimaryDestination"
                                            style="justify-content: flex-start; padding: 20px;">
                                            <v-icon left>{{ (primaryDestination && primaryDestination.icon) ||
                                                'mdi-account-search' }}</v-icon>
                                            <div style="text-align: left;">
                                                <div style="font-weight: 600;">{{ (primaryDestination &&
                                                    primaryDestination.title) || 'Open Destination' }}</div>
                                                <div style="font-size: 12px; opacity: 0.8;">{{
                                                    (primaryDestination && primaryDestination.description) || uiPrimaryDestinationFallbackDescription }}</div>
                                            </div>
                                        </v-btn>

                                        <!-- Go to Contact Import History -->
                                        <v-btn color="secondary" large outlined @click="goToImportHistory"
                                            style="justify-content: flex-start; padding: 20px;">
                                            <v-icon left>mdi-history</v-icon>
                                            <div style="text-align: left;">
                                                <div style="font-weight: 600;">{{ uiHistoryTitle }}</div>
                                                <div style="font-size: 12px; opacity: 0.8;">{{ uiHistoryDescription }}</div>
                                            </div>
                                        </v-btn>

                                        <!-- Import More Contacts (only for Excel imports) -->
                                        <v-btn v-if="isExcelImport" color="grey darken-1" large outlined @click="startNewImport"
                                            style="justify-content: flex-start; padding: 20px;">
                                            <v-icon left>mdi-plus</v-icon>
                                            <div style="text-align: left;">
                                                <div style="font-weight: 600;">{{ uiMoreActionTitle }}</div>
                                                <div style="font-size: 12px; opacity: 0.8;">{{ uiMoreActionDescription }}</div>
                                            </div>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </v-stepper-content>

                    </v-stepper-items>
                    </v-stepper>
                </div>
            </v-card-text>
            </v-container>
        </m-pageframe>

        <!-- Gentle Name Capitalization Dialog -->
        <GentleNameCapitalizationDialog
            ref="gentleNameCapitalizationDialog"
            :organization-id="$route.params.organization_id"
            :import-id="import_id"
            :import-type="import_type"
            @applied="onGentleCapitalizationApplied"
        />
    </div>
</template>

<script>
import ContactImportPreviewTable from '@/components/contacts/contact-import-preview-table.vue';
import ColumnMappingComponent from '@/components/common/column-mapping-component.vue';
import ImportProcessProgress from '@/components/common/import-process-progress.vue';
import ConsolidatedImportMixin from '@/mixins/imports/consolidated-import-mixin.js';
import ImportRowSelectionMixin from '@/mixins/imports/import-row-selection-mixin.js';
import ImportPhoneValidationMixin from '@/mixins/imports/import-phone-validation-mixin.js';

export default {
    mixins: [ConsolidatedImportMixin, ImportRowSelectionMixin, ImportPhoneValidationMixin],

    props: {
        // Allow parent components to override the import type
        overrideImportType: {
            type: String,
            default: null
        },
        // Customization props to tailor component for contact vs contact-list vs recipient
        pageCopy: {
            type: Object,
            default: () => ({})
        },
        fieldDefinitions: {
            type: Object,
            default: () => ({})
        },
        previewConfig: {
            type: Object,
            default: () => ({})
        },
        // Target information props
        targetListName: {
            type: String,
            default: null
        },
        targetBulkMessageName: {
            type: String,
            default: null
        },
        // For bulk-message-recipients: the target bulk message object (passed from parent)
        targetBulkMessage: {
            type: Object,
            default: null
        },
        // Import execution mode: 'add-or-update' or 'remove'
        importExecutionMode: {
            type: String,
            default: 'add-or-update'
        },

    },

    components: {
        ContactImportPreviewTable,
        ColumnMappingComponent,
        ImportProcessProgress,
        GentleNameCapitalizationDialog: () => import('@/components/imports/gentle-name-capitalization-dialog.vue')
    },

    data() {
        return {
            expansion_panel: 0,

            // Contact-specific data (common data is provided by ConsolidatedImportMixin)
            // Validation rules
            file_rules: [
                v => !!v || 'File is required',
                v => !v || v.size < 5242880 || `File size should be less than 5 MB`,
                v => !v || v.name.toLowerCase().endsWith('.xlsx') || 'File must be Excel format (.xlsx)'
            ],

            // Contact-specific field definitions
            requiredFields: [
                {
                    key: 'phone_number',
                    label: 'Phone Number',
                    description: 'Phone number(s) for the contact - you can select multiple columns',
                    multiple: true,
                    required: true,
                    hint: 'Select one or more columns containing phone numbers'
                },
                {
                    key: 'first_name',
                    label: 'First Name',
                    description: 'Contact\'s first name',
                    multiple: false,
                    required: true,
                    hint: 'Select the column containing first names'
                },
                {
                    key: 'last_name',
                    label: 'Last Name',
                    description: 'Contact\'s last name',
                    multiple: false,
                    required: true,
                    hint: 'Select the column containing last names'
                }
            ],
            optionalFields: [
                { key: 'email', label: 'Email', description: 'Contact\'s email address', multiple: false, required: false, hint: 'Select the column containing email addresses' },
                { key: 'birthdate', label: 'Birthdate', description: 'Contact\'s birthdate', multiple: false, required: false, hint: 'Select the column containing birthdates (supports MM/DD/YYYY, MM/DD/YY, or YYYY-MM-DD formats)' },
                { key: 'notes', label: 'Notes', description: 'Contact\'s Notes', multiple: false, required: false, hint: 'Select the column containing notes about the contact' },
            ],

            // Beta feature codes
            beta_feature_codes: {
                gentle_name_capitalization: 'beta-gentle-name-capitalization',
            },

        }
    },

    watch: {
        // Emit event when bulk message ID becomes available from import_meta_data
        targetBulkMessageId: {
            immediate: true,
            handler(new_bulk_message_id) {
                if (new_bulk_message_id && this.isBulkMessageRecipientsImport)
                {
                    this.$emit('bulk-message-id-available', new_bulk_message_id);
                }
            }
        }
    },

    computed: {
        isCanadianOrganization() {
            return this.$rc.$state?.profile?.organization_account?.country_and_subdivision?.startsWith('CA');
        },
        // Check if the gentle name capitalization beta feature is "new" (within date range)
        isGentleCapsFeatureNew() {
            return this.$rc.isBetaFeatureNew(this.beta_feature_codes.gentle_name_capitalization);
        },
        // Check if this is a bulk-message-recipients import
        isBulkMessageRecipientsImport() {
            return this.import_type === 'bulk-message-recipients';
        },
        // Check if the import source is Excel (to conditionally show sheet information)
        isExcelImport() {
            return !this.import_meta_data || !this.import_meta_data.import_data_source || this.import_meta_data.import_data_source === 'excel';
        },
        // Get the target bulk message ID for the import
        targetBulkMessageId() {
            // From query params (new import)
            if (this.$route.query.bulk_message_id)
            {
                return this.$route.query.bulk_message_id;
            }
            // From import metadata (existing import)
            const add_to = this.import_meta_data && this.import_meta_data.add_import_result_to ? this.import_meta_data.add_import_result_to : null;
            const bulk_ids = add_to && Array.isArray(add_to.bulk_message_ids) && add_to.bulk_message_ids.length > 0 ? add_to.bulk_message_ids : null;
            if (bulk_ids)
            {
                return bulk_ids[0];
            }
            return null;
        },
        // Check if the bulk message is in a valid state for importing recipients
        isBulkMessageInEditState() {
            if (!this.isBulkMessageRecipientsImport)
            {
                return true; // Not a bulk message import, always valid
            }
            if (!this.targetBulkMessage)
            {
                return true; // No data yet, assume valid
            }
            return this.targetBulkMessage.state === 'edit';
        },
        // Required by ConsolidatedImportMixin - Backend import type
        import_type() {
            // If parent component provides an override, use it
            if (this.overrideImportType) {
                return this.overrideImportType;
            }
            return 'contact';
        },
        // Allow props to override field definitions
        computedRequiredFields() {
            return (this.fieldDefinitions && this.fieldDefinitions.requiredFields) ? this.fieldDefinitions.requiredFields : this.requiredFields;
        },
        computedOptionalFields() {
            return (this.fieldDefinitions && this.fieldDefinitions.optionalFields) ? this.fieldDefinitions.optionalFields : this.optionalFields;
        },
        // Prop-driven overrides for page copy
        uiPageTitle() {
            return (this.pageCopy && this.pageCopy.pageTitle) ? this.pageCopy.pageTitle : this.page_title;
        },
        uiInstructionsText() {
            return (this.pageCopy && this.pageCopy.instructionsText) ? this.pageCopy.instructionsText : this.instructionsText;
        },
        uiPreviewTableTitle() {
            return (this.previewConfig && this.previewConfig.tableTitle) ? this.previewConfig.tableTitle : this.preview_table_title;
        },

        // Extract target names from import metadata or use props
        computedTargetListName() {
            // First check import metadata (most reliable source)
            if (this.import_meta_data && this.import_meta_data.target_list_names) {
                return this.import_meta_data.target_list_names.replace(/\|/g, ', ');
            }
            // Fallback to prop if provided
            if (this.targetListName) {
                return this.targetListName;
            }
            return null;
        },

        computedTargetBulkMessageName() {
            // First check import metadata (most reliable source)
            if (this.import_meta_data && this.import_meta_data.target_bulk_message_names) {
                return this.import_meta_data.target_bulk_message_names.replace(/\|/g, ', ');
            }
            // Fallback to prop if provided
            if (this.targetBulkMessageName) {
                return this.targetBulkMessageName;
            }
            return null;
        },
        // Preview UI customization via props
        uiContactSummaryCards() {
            return (this.previewConfig && this.previewConfig.summaryCards) ? this.previewConfig.summaryCards : this.contactSummaryCards;
        },
        uiContactCustomColumns() {
            return (this.previewConfig && this.previewConfig.customColumns) ? this.previewConfig.customColumns : this.contactCustomColumns;
        },
        uiContactTableHeaders() {
            return this.contactTableHeaders;
        },

        // Progress/copy overrides
        uiUploadProgressDescription() {
            return (this.pageCopy && this.pageCopy.uploadProgressDescription) ? this.pageCopy.uploadProgressDescription : this.uploadProgressDescription;
        },
        uiExecutionProgressTitle() {
            return (this.pageCopy && this.pageCopy.executionProgressTitle) ? this.pageCopy.executionProgressTitle : this.executionProgressTitle;
        },
        uiExecutionProgressDescription() {
            return (this.pageCopy && this.pageCopy.executionProgressDescription) ? this.pageCopy.executionProgressDescription : this.executionProgressDescription;
        },
        uiExecutionStatusMessage() {
            return (this.pageCopy && this.pageCopy.executionStatusMessage) ? this.pageCopy.executionStatusMessage : this.executionStatusMessage;
        },
        uiCompletionTitle() {
            return (this.pageCopy && this.pageCopy.completionTitle) ? this.pageCopy.completionTitle : this.completionTitle;
        },
        uiCompletionDescription() {
            return (this.pageCopy && this.pageCopy.completionDescription) ? this.pageCopy.completionDescription : this.completionDescription;
        },
        actionNoun() {
            return this.isRemoveMode ? 'Removal' : 'Import';
        },
        actionNounLower() {
            return this.isRemoveMode ? 'removal' : 'import';
        },
        actionVerbLower() {
            return this.isRemoveMode ? 'remove' : 'import';
        },
        actionVerbPast() {
            return this.isRemoveMode ? 'removed' : 'imported';
        },
        uiBackToHistoryTitle() {
            return this.isRemoveMode ? 'Back to Removal History' : 'Back to Import Search';
        },
        uiStepUploadTitle() {
            return this.isRemoveMode ? 'Upload Removal File' : 'Upload File';
        },
        uiStepReviewTitle() {
            return this.isRemoveMode ? 'Review Removals' : 'Review';
        },
        uiContinueToPreviewLabel() {
            return this.isRemoveMode ? 'Continue to Removal Preview' : 'Continue to Preview';
        },
        uiStepCompleteTitle() {
            return `${this.actionNoun} Complete`;
        },
        uiInstructionsFollowUp() {
            if (this.isRemoveMode) {
                return 'Only recipient associations are removed; contact records are not deleted.';
            }
            return 'If a record already exists in the system, the import will update the existing record rather than creating a new one.';
        },
        uiFormatRequirementsTitle() {
            return this.isRemoveMode ? 'Removal File Format Requirements' : 'Excel File Format Requirements';
        },
        uiReviewPanelTitle() {
            return this.isRemoveMode ? 'Please review the removal details before executing' : 'Please review the below before executing';
        },
        uiToolsLabel() {
            return this.isRemoveMode ? 'Removal Tools' : 'Import Tools';
        },
        uiStartNewActionLabel() {
            return this.isRemoveMode ? 'Start New Removal' : 'Start New Upload';
        },
        uiHistoryTitle() {
            return this.isRemoveMode ? 'View Removal History' : 'View Import History';
        },
        uiHistoryDescription() {
            return this.isRemoveMode ? 'See all removals for this organization' : 'See all imports for this organization';
        },
        uiMoreActionTitle() {
            return this.isRemoveMode ? 'Remove More' : 'Import More';
        },
        uiMoreActionDescription() {
            return this.isRemoveMode ? 'Start a new removal' : 'Start a new import';
        },
        uiPrimaryDestinationFallbackDescription() {
            return `Open the primary destination for this ${this.actionNounLower}`;
        },


        // Check if user can upload file
        can_upload_file() {
            return this.excel_file && !this.uploading;
        },

        // Check if user can proceed to column mapping
        canProceedToColumnMapping() {
            return this.import_state === 'uploaded' && this.sheet_information;
        },



        // Check if user can proceed to execution
        canProceedToExecution() {
            const nonExecutableStates = ['cancelled', 'completed', 'failed'];

            // Basic checks
            if (!this.preview_data || this.importing || this.is_read_only_mode || nonExecutableStates.includes(this.import_state)) {
                return false;
            }

            // Check if there are any selected rows
            if (!this.selected_rows || this.selected_rows.length === 0) {
                return false;
            }

            // Check if all selected rows are valid for import (have will_be_imported = true)
            const validSelectedRows = this.selected_rows.filter(row => row.will_be_imported);
            if (validSelectedRows.length === 0) {
                return false;
            }

            // For bulk message recipient imports, check if bulk message is in edit state
            if (!this.isBulkMessageInEditState)
            {
                return false;
            }

            return true;
        },

        // Get execution disabled reason for user feedback
        executionDisabledReason() {
            const nonExecutableStates = ['cancelled', 'completed', 'failed'];

            if (!this.preview_data) {
                return 'No preview data available';
            }

            if (this.importing) {
                return `${this.actionNoun} is currently running`;
            }

            if (this.is_read_only_mode) {
                return 'Read-only access';
            }

            if (nonExecutableStates.includes(this.import_state)) {
                return `${this.actionNoun} is ${this.import_state}`;
            }

            if (!this.selected_rows || this.selected_rows.length === 0) {
                return `No rows selected for ${this.actionNounLower}`;
            }

            const validSelectedRows = this.selected_rows.filter(row => row.will_be_imported);
            if (validSelectedRows.length === 0) {
                return 'All selected rows have validation errors';
            }

            if (!this.isBulkMessageInEditState)
            {
                return `Bulk message must be in edit state to ${this.actionVerbLower} recipients`;
            }

            return '';
        },

        // Override ConsolidatedImportMixin - Contact imports have different cancellation rules
        can_cancel_import() {
            const nonCancellableStates = ['cancelled', 'completed', 'failed'];
            return !nonCancellableStates.includes(this.import_state);
        },

        // Import type specific message for the fourth column
        importTypeSpecificMessage() {
            if (this.import_type === 'bulk-message-recipients') {
                // Show different message based on execution mode
                if (this.isRemoveMode) {
                    return {
                        icon: 'mdi-account-minus',
                        color: 'warning',
                        title: 'Recipients matching phone numbers will be removed from this bulk message.',
                        subtitle: 'Only the recipient association is removed.'
                    };
                }
                return {
                    icon: 'mdi-email-multiple',
                    color: 'purple',
                    title: 'Recipients that are not currently in the selected bulk message will be added to the recipient list.',
                    subtitle: 'Any identified existing recipients will only have their contact information and any bulk message extra fields updated.'
                };
            } else if (this.import_type === 'contact-list') {
                return {
                    icon: 'mdi-format-list-bulleted',
                    color: 'blue',
                    title: 'Members that are not currently in the selected list will be added to list.',
                    subtitle:  'Any identified existing members will only have their contact information updated.'
                };
            }
            return null;
        },

        // Check if in remove mode
        isRemoveMode() {
            return this.importExecutionMode === 'remove';
        },

        // Primary destination info: prefer explicit query params, then import metadata destinations, else contact search
        primaryDestination() {
            // 1) From explicit query (new imports started from a specific destination)
            if (this.$route.query.bulk_message_id) {
                return {
                    icon: 'mdi-email-multiple',
                    title: 'Go to Bulk Message',
                    description: `Return to the bulk message that was used for this ${this.actionNounLower}`,
                    navigate: () => this.$router.push({
                        name: 'view-bulk-message',
                        params: {
                            organization_id: this.$route.params.organization_id,
                            bulk_message_id: this.$route.query.bulk_message_id
                        }
                    })
                };
            }
            if (this.$route.query.list_id) {
                return {
                    icon: 'mdi-format-list-bulleted',
                    title: 'Go to List',
                    description: this.isRemoveMode
                        ? 'Return to the list that these contacts were removed from'
                        : 'Return to the list that these contacts were added to',
                    navigate: () => this.$router.push({
                        name: 'edit-list',
                        params: { list_id: this.$route.query.list_id }
                    })
                };
            }

            // 2) From import metadata when viewing an existing import from search (no query params)
            const add_to = this.import_meta_data && this.import_meta_data.add_import_result_to ? this.import_meta_data.add_import_result_to : null;
            const bulk_ids = add_to && Array.isArray(add_to.bulk_message_ids) && add_to.bulk_message_ids.length > 0 ? add_to.bulk_message_ids : null;
            if (bulk_ids) {
                const bulk_message_id = bulk_ids[0];
                return {
                    icon: 'mdi-email-multiple',
                    title: 'Go to Bulk Message',
                    description: `Open the bulk message for this ${this.actionNounLower}`,
                    navigate: () => this.$router.push({
                        name: 'view-bulk-message',
                        params: {
                            organization_id: this.$route.params.organization_id,
                            bulk_message_id
                        }
                    })
                };
            }

            const list_ids = add_to && Array.isArray(add_to.list_ids) && add_to.list_ids.length > 0 ? add_to.list_ids : null;
            if (list_ids) {
                const list_id = list_ids[0];
                return {
                    icon: 'mdi-format-list-bulleted',
                    title: 'Go to List',
                    description: `Open the contact list for this ${this.actionNounLower}`,
                    navigate: () => this.$router.push({
                        name: 'edit-list',
                        params: { list_id }
                    })
                };
            }

            // 3) Fallback to contact search
            return {
                icon: 'mdi-account-search',
                title: 'Go to Contact Search',
                description: 'Search and manage all contacts in this organization',
                navigate: () => this.$router.push({
                    name: 'fulltext-search-contacts',
                    params: { organization_id: this.$route.params.organization_id }
                })
            };
        },

        // Check if user can proceed to preview
        canProceedToPreview() {
            const allowedStates = ['uploaded', 'preview-ready', 'failed', 'cancelled'];
            return this.import_id && (allowedStates.includes(this.import_state) || this.preview_ready);
        },

        // Check if user can edit a step
        canEditStep() {
            return (step) => {
                if (!this.has_write_access) return false;
                if (this.uploading || this.generating_preview || this.importing || this.import_locked) return false;

                const isCompleted = this.import_state === 'completed';

                switch (step) {
                    case 1:
                        return false; // Can't go back to upload once started
                    case 2:
                        // Can only edit column mapping if we're in uploaded state and haven't proceeded to preview yet
                        return this.import_state === 'uploaded' && this.sheet_information && this.current_step <= 2;
                    case 3:
                        return this.canContinueToPreview && !isCompleted;
                    case 4:
                        return isCompleted;
                    default:
                        return false;
                }
            };
        },

        // Instructions text based on import type
        instructionsText() {
            // Generic fallback; parents pass pageCopy.instructionsText for specific flows
            return this.isRemoveMode
                ? 'Use this tool to remove recipients using an Excel file.'
                : 'Use this tool to import records from an Excel file.';
        },

        // Entity ID for the import (list_id or bulk_message_id)
        entityId() {
            return this.$route.query.list_id || this.$route.query.bulk_message_id || null;
        },

        // Dynamic page title based on import type
        page_title() {
            // Generic fallback; parents pass pageCopy.pageTitle for specific flows
            return this.isRemoveMode ? 'Remove Contacts' : 'Import Contacts';
        },

        canContinueToPreview() {
            return this.can_continue_to_preview && this.column_mapping_data && !this.saving_column_mapping;
        },


        uploadProgressDescription() {
            return 'Please wait while we upload and process your file.';
        },

        // Execution progress messages
        executionProgressTitle() {
            return this.isRemoveMode ? 'Removing...' : 'Importing...';
        },

        executionProgressDescription() {
            return this.isRemoveMode ? 'Please wait while we remove your data.' : 'Please wait while we import your data.';
        },

        executionStatusMessage() {
            return this.isRemoveMode ? 'Removing...' : 'Importing...';
        },

        // Completion messages
        completionTitle() {
            return this.isRemoveMode ? 'Removal Complete!' : 'Import Complete!';
        },

        completionDescription() {
            return this.isRemoveMode ? 'Your removal has completed successfully.' : 'Your import has completed successfully.';
        },

        contactSummaryCards() {
            if (!this.preview_data) {
                return [];
            }

            const cards = [];

            // Always show total records
            if (this.preview_data.total_records >= 0) {
                cards.push({ key: 'total_records', label: 'Total Records', cols: 3 });
            }

            // Show valid records count, always show
            if (this.preview_data.valid_records_count >= 0) {
                cards.push({ key: 'valid_records_count', label: 'Valid Records', cols: 3 });
            }

            // Show contacts to create if > 0
            if (this.preview_data.total_contacts_to_create > 0) {
                cards.push({ key: 'total_contacts_to_create', label: 'Contacts Available to Create', cols: 3 });
            }

            // Show contacts to update if > 0
            if (this.preview_data.total_contacts_to_update > 0) {
                cards.push({ key: 'total_contacts_to_update', label: 'Contacts Available to Update', cols: 3 });
            }

            // Show list members to add if > 0
            if (this.preview_data.total_list_members_to_add > 0) {
                cards.push({ key: 'total_list_members_to_add', label: 'List Members Available to Add', cols: 3 });
            }

            // Show bulk message recipients if > 0
            if (this.preview_data.total_bulk_message_recipients_to_add > 0) {
                cards.push({ key: 'total_bulk_message_recipients_to_add', label: 'Bulk Message Recipients Available to Add', cols: 3 });
            }

            // Show errors if any
            if (this.preview_data.error_records > 0) {
                cards.push({ key: 'error_records', label: 'Errors', cols: 3 });
            }

            // Show warnings if any
            if (this.preview_data.warning_records > 0) {
                cards.push({ key: 'warning_records', label: 'Warnings', cols: 3 });
            }

            return cards;
        },

        contactCustomColumns() {
            // Generic default; parents provide columns via previewConfig
            return [
                { key: 'select', type: 'custom' }
            ];
        },

        // Warning logic for large number of contact updates
        above_warn_change_contact_threshold() {
            if (!this.preview_data || !this.preview_data.total_contacts_to_update) {
                return false;
            }

            const total_contacts_to_update = this.preview_data.total_contacts_to_update;
            const valid_records = this.preview_data.valid_records_count || 0;

            // Show warning if more than 10 contacts would be updated AND it's more than 50% of valid records
            return total_contacts_to_update > 10 && valid_records > 0 && (total_contacts_to_update / valid_records) > 0.5;
        },

        above_contact_threshold_warning_message() {
            return 'Warning: This import would <b><u>change</u></b> the data of a large number of <b><u>pre-existing contacts</u></b>.  The data of more than <b><u>' + this.pretty_percent_updated_contacts + '</u></b> would be altered. This frequently happens when the imported file contains an error.  For example, the names and phone numbers are mis-aligned in your spreadsheet (even by one row).  Please check that your import does not have a problem <b><u>very carefully</u></b> before proceeding.';
        },

        pretty_percent_updated_contacts() {
            if (!this.preview_data || !this.preview_data.total_contacts_to_update || !this.preview_data.valid_records_count) {
                return '0%';
            }

            const percentage = (this.preview_data.total_contacts_to_update / this.preview_data.valid_records_count) * 100;
            return Math.round(percentage) + '%';
        },

        contactTableHeaders() {
            // Filter columns to only show those with data or core fields
            return this.previewConfig.tableHeaders.filter(column => {
                if (column.alwaysShow) return true;

                // Special handling for extra fields
                if (column.value === 'rc_extra_fields') {
                    // If we have the import definition, strict check if any extra fields were mapped
                    if (this.import_meta_data && this.import_meta_data.raw_import_definition && Array.isArray(this.import_meta_data.raw_import_definition.extra_fields_columns)) {
                        return this.import_meta_data.raw_import_definition.extra_fields_columns.length > 0;
                    }
                    // Fallback to data check if definition is missing
                }

                return this.hasDataForColumn(column.value);
            });
        },

    },

    mounted() {
        // Use ConsolidatedImportMixin initialization
        this.initializeComponent();
    },

    methods: {
        /**
         * Dismiss the gentle name capitalization tooltip when the import tools menu is opened.
         */
        onImportToolsMenuToggle(is_open) {
            if (is_open && this.$refs.gentle_caps_tooltip) {
                this.$refs.gentle_caps_tooltip.onDismiss();
            }
        },
        // Override ConsolidatedImportMixin to add query parameters to form data
        addAdditionalUploadData(form_data) {
            if (this.$route.query.list_id) {
                form_data.append('list_id', this.$route.query.list_id);
            }
            if (this.$route.query.bulk_message_id) {
                form_data.append('bulk_message_id', this.$route.query.bulk_message_id);
            }
            if (this.importExecutionMode) {
                form_data.append('import_execution_mode', this.importExecutionMode);
            }
        },

        /**
         * Open the gentle name capitalization dialog
         */
        showGentleNameCapitalizationDialog() {
            if (this.$refs.gentleNameCapitalizationDialog) {
                this.$refs.gentleNameCapitalizationDialog.open();
            }
        },

        /**
         * Handle when gentle capitalization is applied - backend handles regeneration atomically
         */
        onGentleCapitalizationApplied() {
            // Backend handles regeneration atomically - just reload import data
            this.loadExistingImport();
        },

        // Check if any row has data for the given column
        hasDataForColumn(columnValue) {
            if (!this.preview_data || !this.preview_data.preview_rows) {
                return false;
            }

            return this.preview_data.preview_rows.some(row => {
                const value = row[columnValue];
                return value !== null && value !== undefined && value !== '';
            });
        },

        // Override goToStep to add validation
        goToStep(step) {
            // Validate that we can navigate to the requested step
            if (!this.canEditStep(step)) {
                return;
            }

            this.current_step = step;

            // Handle any step-specific logic
            switch (step) {
                case 2:
                    // Column mapping step - load sheet information
                    if (this.import_id && !this.sheet_information && !this.loading_sheet_information) {
                        this.loadSheetInformation();
                    }
                    break;
                case 3:
                    // Preview step - start preview generation if needed
                    if (this.import_id && !this.preview_ready && !this.generating_preview && !this.preview_failed) {
                        this.startPreviewGeneration();
                    }
                    break;
                case 4:
                    // Completion step - no specific logic needed
                    break;
            }
        },

        // Override ConsolidatedImportMixin to handle contact-specific state transitions
        handleImportStateTransition() {
            this.import_locked = false;
            this.import_locked_reason = '';

            // Handle contact-specific state transitions
            switch (this.import_state) {
                case 'uploading':
                    this.current_step = 1;
                    this.uploading = false;
                    this.upload_errors = ['Upload process was interrupted. Please try uploading the file again.'];
                    break;

                case 'uploaded':
                    this.current_step = 2;
                    if (!this.sheet_information && !this.loading_sheet_information) {
                        this.loadSheetInformation();
                    }
                    break;


                case 'preview-ready':
                    this.current_step = 3;
                    if (!this.preview_ready) {
                        this.loadPreviewData();
                    }
                    break;

                case 'importing':
                    this.applyImportInProgressState();
                    break;

                case 'completed':
                    this.current_step = 4;
                    this.importing = false;
                    break;

                case 'cancelled':
                    this.current_step = 3;
                    if (!this.preview_ready) {
                        this.loadPreviewData();
                    }
                    break;

                case 'failed':
                    this.current_step = 3;
                    if (!this.preview_ready) {
                        this.loadPreviewData();
                    }
                    this.$rc.snackbar(`${this.actionNoun} failed. Please review the errors in the preview.`, 'error');
                    break;
            }
        },

        // Override ConsolidatedImportMixin - Contact imports need specific navigation
        goBackToImportSearch() {
            this.goBack();
        },

        // Continue to preview (trigger column mapping save) - removed $refs usage
        onClickContinueToPreview() {
            this.onContinueToPreview();
        },


        onContinueToPreview() {
            // Trigger column mapping save directly
            this.saveColumnMapping();
        },

        // === COLUMN MAPPING API METHODS ===

        onMappingFailed() {

            this.handleImportStateTransition();
            this.goToStep(3);
            this.preview_failed = true;
            this.preview_error_message = `${this.actionNoun} failed due to validation errors. Please review the errors below and fix your data.`;
        },

        // Preview generation callbacks - ConsolidatedImportMixin handles most of this
        onPreviewComplete() {
            this.generating_preview = false;
            this.preview_ready = true;
            // Navigate to preview step
            this.current_step = 3;
        },

        // Import execution - use ConsolidatedImportMixin method
        onCompleteImport() {
            this.current_step = 4;
            this.onExecuteImport(); // Use mixin method
        },

        // Override ConsolidatedImportMixin execution completion
        onExecutionComplete() {
            this.importing = false;
            this.import_state = 'completed';
            this.current_step = 4;

            this.loadExistingImport();
        },

        onClickDownloadEnrichedDataFromPreview() {
            if (!this.import_meta_data || !this.import_meta_data.import_id) return;
            this.$rc.rest({
                spinner: true,
                endpoint: `GET: /api/v1/imports/${this.import_type}/download-enriched-data/:organization_id/:import_id`,
                params: {
                    organization_id: this.$route.params.organization_id,
                    import_id: this.import_meta_data.import_id
                },
                on_success: (data) => {
                    window.open(data.download_url, '_blank');
                },
                on_error: 'Unable to download enriched data file'
            });
        },

        // Override ConsolidatedImportMixin execution failure
        onExecutionFailed(errorData) {
            this.importing = false;
            this.import_state = 'failed';

            // Parse the error - errorData may be a full PTE object, an object with exception field, or a string
            let error_message = 'Execution failed unexpectedly';

            if (typeof errorData === 'string')
            {
                error_message = errorData;
            }
            else if (errorData && typeof errorData === 'object')
            {
                // Try to extract the exception field from the PTE object
                if (errorData.exception)
                {
                    error_message = errorData.exception;
                }
                else if (errorData.message)
                {
                    error_message = errorData.message;
                }
            }

            this.execution_errors = [error_message];
        },

        // Override ConsolidatedImportMixin execution request params to provide selected rows
        getExecutionRequestParams() {
            return {
                legal_attestation_specimen: '', // Contact imports don't require attestation, but backend expects this parameter
                selected_row_numbers: this.getSelectedRowNumbers() // Include only selected rows in the import
            };
        },

        // Column mapping callbacks
        onSheetChanged() {
            // Handle sheet change if needed
        },

        onMappingChanged() {
            // Handle mapping change if needed
        },

        onIntermediateCreated(data) {
            // Handle intermediate data creation
            this.intermediate_data = data;
        },


        // Override ConsolidatedImportMixin - Contact imports navigate to step 3 after mapping
        onMappingSaved() {
            this.handleImportStateTransition();
            this.goToStep(3);
        },



        // Navigation methods
        
        // Back button navigation - respects return_to query param
        goBack() {
            // Check if user came from edit list view - go back to the list
            if (this.$route.query.return_to === 'edit-list' && this.$route.query.list_id) {
                this.$router.push({
                    name: 'edit-list',
                    params: {
                        list_id: this.$route.query.list_id
                    }
                });
                return;
            }

            // Check if user came from view bulk message - go back to the bulk message
            if (this.$route.query.return_to === 'view-bulk-message' && this.$route.query.bulk_message_id) {
                this.$router.push({
                    name: 'view-bulk-message',
                    params: {
                        organization_id: this.$route.params.organization_id,
                        bulk_message_id: this.$route.query.bulk_message_id
                    }
                });
                return;
            }

            // Default: go to import history
            this.goToImportHistory();
        },

        // History button navigation - always goes to import history
        goToImportHistory() {
            // Use parent-provided route name; fallback to generic contact import search
            const route_name = (this.pageCopy && this.pageCopy.historyRouteName) || 'contact-import-search';
            const query = {};
            // Pass bulk_message_id if available so import history can filter by it
            if (this.$route.query.bulk_message_id) {
                query.bulk_message_id = this.$route.query.bulk_message_id;
            }
            this.$router.push({
                name: route_name,
                params: { organization_id: this.$route.params.organization_id },
                query
            });
        },

        startNewImport() {
            // Build query params - preserve mode, return_to, and entity ID for a new import
            const query = {};

            // Preserve mode (e.g., 'remove' for removal imports)
            if (this.$route.query.mode) {
                query.mode = this.$route.query.mode;
            }

            // Preserve return_to for navigation after completion
            if (this.$route.query.return_to) {
                query.return_to = this.$route.query.return_to;
            }

            // Get the entity ID from import_meta_data (most reliable) or fall back to query params
            const add_to = this.import_meta_data && this.import_meta_data.add_import_result_to ? this.import_meta_data.add_import_result_to : null;

            // Check for bulk message ID
            const bulk_ids = add_to && Array.isArray(add_to.bulk_message_ids) && add_to.bulk_message_ids.length > 0 ? add_to.bulk_message_ids : null;
            if (bulk_ids) {
                query.bulk_message_id = bulk_ids[0];
            } else if (this.$route.query.bulk_message_id) {
                query.bulk_message_id = this.$route.query.bulk_message_id;
            }

            // Check for list ID
            const list_ids = add_to && Array.isArray(add_to.list_ids) && add_to.list_ids.length > 0 ? add_to.list_ids : null;
            if (list_ids) {
                query.list_id = list_ids[0];
            } else if (this.$route.query.list_id) {
                query.list_id = this.$route.query.list_id;
            }

            // Navigate to a new import (same route, without import_id param)
            this.$router.push({
                name: this.$route.name,
                params: { organization_id: this.$route.params.organization_id },
                query
            });

            // Reload the page to clear any state
            window.location.reload();
        },

        onClickDownloadTemplate() {
            // Use generic contact import template
            const download_url = this.$rc.getAPIEndpoint('/contact/download-import-template');
            window.open(download_url, '_blank');
        },

        // Override ConsolidatedImportMixin - Contact imports need confirmation dialog (similar to consent import)
        cancelImport() {
            const cancel_action_label = `Cancel ${this.actionNoun}`;
            const cancel_body = `Are you sure you want to cancel this ${this.actionNounLower}? This action cannot be undone and the ${this.actionNounLower} will be marked as cancelled.`;
            const cancel_ok_label = `Yes, Cancel ${this.actionNoun}`;
            const cancel_keep_label = this.isRemoveMode ? `Continue ${this.actionNoun}` : 'Keep Import';
            this.$rc.confirm({
                title: cancel_action_label,
                body: cancel_body,
                btn_text_ok: cancel_ok_label,
                btn_text_cancel: cancel_keep_label,
                on_ok: () => {
                    this.$rc.rest({
                        'spinner': true,
                        'endpoint': `POST: /api/v1/imports/${this.import_type}/cancel/${this.$route.params.organization_id}/${this.import_id}`,
                        'params': {},
                        'on_success': () => {
                            this.import_state = 'cancelled';
                            this.handleImportStateTransition();
                            this.$rc.snackbar(`${this.actionNoun} cancelled successfully`, 'success');
                        },
                        'on_error': (error) => {
                            this.$rc.snackbar(`Failed to cancel ${this.actionNounLower}: ${error}`, 'error');
                        }
                    });
                }
            });
        },

        // Execute import with confirmation
        executeImport() {
            let body = `Are you sure you want to perform this bulk ${this.actionNounLower}?`;
            if (this.above_warn_change_contact_threshold) {
                body += '  <br><br>' + this.above_contact_threshold_warning_message;
            }
            // Preview data available for debugging if needed
            // Count the number of "include" state rows
            const includeCount = this.selected_rows.length;
            body += '  <br><br>Number of records selected to be ' + this.actionVerbPast + ': <b>' + includeCount + '</b>';

            // If user hasn't initiated validation, and there are unknown phone validation statuses in the selected rows, warn them
            if (!this.isRemoveMode && !this.validating_phone_numbers && !this.phone_validation_batch_id) {
                const unknown_count = (this.selected_rows || []).filter(row => {
                    const status = ((row && row.rc_validation_status) || '').toString().trim().toLowerCase();
                    return status === 'unknown';
                }).length;
                if (unknown_count > 0) {
                    const plural = unknown_count === 1 ? '' : 's';
                    body += '  <br><br><b>Note:</b> ' + unknown_count + ' selected phone number' + plural + ' ha' + (unknown_count === 1 ? 's' : 've') + ' an <b>unknown</b> validation status. This means some numbers may not be valid. You can proceed, but you may want to click "' + this.uiToolsLabel + '" → "Validate Unknown Numbers" first.';
                }
            }

            this.$rc.confirm({
                title: `Perform ${this.actionNoun}`,
                body,
                btn_text_ok: `Yes, Run ${this.actionNoun}`,
                btn_text_cancel: 'Back to Preview',
                on_ok: () => {
                    this.onCompleteImport();
                }
            });
        },

        getExecutionStepNumber() {
            return 4;
        },

        // Navigate to primary destination based on context
        goToPrimaryDestination() {
            // Prefer parent-provided navigation if present; otherwise use computed fallback
            if (this.primaryDestination && typeof this.primaryDestination.navigate === 'function') {
                this.primaryDestination.navigate();
                return;
            }
            // Fallback (should be rare since primaryDestination always defined)
            this.$router.push({
                name: 'fulltext-search-contacts',
                params: { organization_id: this.$route.params.organization_id }
            });
        },

        // Navigate to contact import history
        goToContactImportHistory() {
            const route_name = (this.pageCopy && this.pageCopy.historyRouteName) || 'contact-import-search';
            this.$router.push({
                name: route_name,
                params: { organization_id: this.$route.params.organization_id }
            });
        }
    }
};
</script>

<style scoped>
.removal-stepper ::v-deep .v-stepper__label {
    white-space: nowrap;
}
</style>
